import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useAuth } from "@/store/AuthContext";

const DEFAULT_GROUP_ID = "g_lisbon_dawn";

// ---------- Auth-scoped enabling ----------
function useEnabled() {
  const { user, loading } = useAuth();
  return { enabled: !!user && !loading, userId: user?.user_id };
}

// ---------- Ads (viewer-aware) ----------
export function useAds(placement) {
  const { userId } = useEnabled();
  return useQuery({
    queryKey: ["ads", placement || "all", userId || "anon"],
    queryFn: async () => {
      const params = placement ? { placement } : {};
      return (await api.get("/ads/slots", { params })).data;
    },
    staleTime: 5 * 60 * 1000,
  });
}

// ---------- Catalog ----------
export function useCategories() {
  return useQuery({
    queryKey: ["categories"],
    queryFn: async () => (await api.get("/categories")).data,
  });
}

export function useLibrary({ category = "all", q = "" } = {}) {
  return useQuery({
    queryKey: ["library", category, q],
    queryFn: async () => {
      const params = {};
      if (category && category !== "all") params.category = category;
      if (q) params.q = q;
      return (await api.get("/habits/library", { params })).data;
    },
  });
}

// ---------- Me (auth) ----------
export function useMyHabits() {
  const { enabled, userId } = useEnabled();
  return useQuery({
    queryKey: ["me", "habits", userId],
    queryFn: async () => (await api.get("/me/habits")).data,
    enabled,
  });
}

export function useMyNotifications() {
  const { enabled, userId } = useEnabled();
  return useQuery({
    queryKey: ["me", "notifications", userId],
    queryFn: async () => (await api.get("/me/notifications")).data,
    enabled,
  });
}

export function useMySegmentation() {
  const { enabled, userId } = useEnabled();
  return useQuery({
    queryKey: ["me", "segmentation", userId],
    queryFn: async () => (await api.get("/me/segmentation")).data,
    enabled,
  });
}

export function useMyGroup() {
  const { enabled, userId } = useEnabled();
  return useQuery({
    queryKey: ["me", "group", userId],
    queryFn: async () => (await api.get("/me/group")).data,
    enabled,
  });
}

export function useMyPendingNudges() {
  const { enabled, userId } = useEnabled();
  return useQuery({
    queryKey: ["me", "nudges", "pending", userId],
    queryFn: async () => (await api.get("/me/nudges/pending")).data,
    enabled,
  });
}

// ---------- Active group resolution ----------
export function useActiveGroupId() {
  const { user } = useAuth();
  const { data: myGroup } = useMyGroup();
  return (user && myGroup?.id) || DEFAULT_GROUP_ID;
}

export function useGroup(groupId) {
  const activeId = useActiveGroupId();
  const id = groupId || activeId;
  return useQuery({
    queryKey: ["group", id],
    queryFn: async () => (await api.get(`/groups/${id}`)).data,
    enabled: !!id,
  });
}

export function useGroupMembers(groupId) {
  const activeId = useActiveGroupId();
  const id = groupId || activeId;
  return useQuery({
    queryKey: ["group", id, "members"],
    queryFn: async () => (await api.get(`/groups/${id}/members`)).data,
    enabled: !!id,
  });
}

// ---------- Viewer timezone ----------
export function useViewerTz() {
  const { user } = useAuth();
  const { data: seg } = useMySegmentation();
  if (user && seg?.timezone) return seg.timezone;
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return "UTC";
  }
}

export function useGroupCalendar(groupId) {
  const activeId = useActiveGroupId();
  const id = groupId || activeId;
  const viewerTz = useViewerTz();
  return useQuery({
    queryKey: ["group", id, "calendar", viewerTz],
    queryFn: async () =>
      (await api.get(`/groups/${id}/calendar`, { params: { viewer_tz: viewerTz } })).data,
    enabled: !!id,
  });
}

export function useGroupOverlap(groupId) {
  const activeId = useActiveGroupId();
  const id = groupId || activeId;
  return useQuery({
    queryKey: ["group", id, "overlap"],
    queryFn: async () => (await api.get(`/groups/${id}/overlap`)).data,
    enabled: !!id,
  });
}

// ---------- Mutations ----------
export function useAddHabit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ template_id, start }) =>
      (await api.post("/me/habits", { template_id, start })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["me", "habits"] });
      qc.invalidateQueries({ queryKey: ["ads"] });
    },
  });
}

export function useRemoveHabit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (template_id) =>
      (await api.delete(`/me/habits/${template_id}`)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["me", "habits"] });
      qc.invalidateQueries({ queryKey: ["ads"] });
    },
  });
}

export function useCreateCustomHabit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body) => (await api.post("/me/custom-habits", body)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["me", "habits"] });
      qc.invalidateQueries({ queryKey: ["library"] });
    },
  });
}

export function useUpdateHabitStart() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ template_id, start }) =>
      (await api.patch(`/me/habits/${template_id}`, { start })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["me", "habits"] }),
  });
}

export function useUpdateNotifications() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (patch) =>
      (await api.patch("/me/notifications", patch)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["me", "notifications"] }),
  });
}

export function useSaveSegmentation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload) =>
      (await api.put("/me/segmentation", payload)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["me", "segmentation"] });
      qc.invalidateQueries({ queryKey: ["ads"] });
    },
  });
}

export function usePatchSegmentation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (patch) =>
      (await api.patch("/me/segmentation", patch)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["me", "segmentation"] });
      qc.invalidateQueries({ queryKey: ["me", "match"] });
      qc.invalidateQueries({ queryKey: ["ads"] });
    },
  });
}

export function useMatchCandidates() {
  const { enabled, userId } = useEnabled();
  return useQuery({
    queryKey: ["me", "match", "candidates", userId],
    queryFn: async () => (await api.get("/me/match/candidates")).data,
    enabled,
  });
}

export function useSwitchGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (group_id) =>
      (await api.post("/me/match/switch", { group_id })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["me", "group"] });
      qc.invalidateQueries({ queryKey: ["group"] });
      qc.invalidateQueries({ queryKey: ["me", "nudges"] });
      qc.invalidateQueries({ queryKey: ["me", "match"] });
    },
  });
}

export function useRecomputeMatch() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => (await api.post("/me/match/recompute")).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["me", "group"] });
      qc.invalidateQueries({ queryKey: ["group"] });
      qc.invalidateQueries({ queryKey: ["me", "nudges"] });
    },
  });
}

export function useMarkOnboarded() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => (await api.post("/me/onboarded")).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["auth"] }),
  });
}

export function useMarkTutorialSeen() {
  return useMutation({
    mutationFn: async () => (await api.post("/me/tutorial-seen")).data,
  });
}

// ---------- Preferences (auto-nudge) ----------
export function useMyPreferences() {
  const { enabled, userId } = useEnabled();
  return useQuery({
    queryKey: ["me", "preferences", userId],
    queryFn: async () => (await api.get("/me/preferences")).data,
    enabled,
  });
}

export function useUpdatePreferences() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (patch) => (await api.patch("/me/preferences", patch)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["me", "preferences"] });
      qc.invalidateQueries({ queryKey: ["me", "inbox"] });
    },
  });
}

export function useMarkHabit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ template_id, status }) => {
      const path = status === "completed" ? "complete" : "skip";
      return (await api.post(`/me/habits/${template_id}/${path}`)).data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["me", "preferences"] });
      qc.invalidateQueries({ queryKey: ["me", "habits"] });
    },
  });
}

export function useMyInbox() {
  const { enabled, userId } = useEnabled();
  return useQuery({
    queryKey: ["me", "inbox", userId],
    queryFn: async () => (await api.get("/me/inbox")).data,
    enabled,
    refetchInterval: 60_000,
  });
}

export function useMarkNudgeSeen() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (nudgeId) => (await api.post(`/me/inbox/${nudgeId}/seen`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["me", "inbox"] }),
  });
}

export function useSendNudge() {
  return useMutation({
    mutationFn: async ({ recipient_id, habit_id, note }) =>
      (await api.post("/nudges", { recipient_id, habit_id, note })).data,
  });
}

// ---------- Helpers ----------
export function useHabitMap() {
  const { data } = useLibrary();
  const list = data || [];
  return Object.fromEntries(list.map((h) => [h.id, h]));
}

export function useMemberMap() {
  const { data } = useGroupMembers();
  const list = data || [];
  return Object.fromEntries(list.map((m) => [m.id, m]));
}

export function useCategoriesMap() {
  const { data } = useCategories();
  const list = data || [];
  return Object.fromEntries(list.map((c) => [c.id, c.label]));
}
